import React from "react";
import "./WhyChooseUs.scss";
import { 
  FaPlane, 
  FaUsers, 
  FaMapMarkedAlt, 
  FaStar, 
  FaShieldAlt, 
  FaHandshake, 
  FaGlobe, 
  FaClock,
  FaCertificate,
  FaLeaf,
  FaHeart,
  FaAward
} from "react-icons/fa";
import { 
  FiCheckCircle, 
  FiMapPin, 
  FiUserCheck, 
  FiLock, 
  FiGlobe, 
  FiCalendar,
  FiTrendingUp,
  FiThumbsUp
} from "react-icons/fi";

const WhyChooseUs = () => {
  const features = [
    {
      icon: <FaCertificate />,
      title: "Government Registered",
      description: "Fully licensed and authorized by Department of Tourism Gilgit-Baltistan (License: DTS-GB-021).",
      stats: "10+ Years Licensed",
      color: "primary"
    },
    {
      icon: <FaUsers />,
      title: "Expert Local Guides",
      description: "Certified mountain guides with extensive local knowledge and international safety training.",
      stats: "50+ Certified Guides",
      color: "secondary"
    },
    {
      icon: <FaShieldAlt />,
      title: "Safety First Approach",
      description: "Comprehensive safety protocols, emergency evacuation plans, and satellite communication.",
      stats: "100% Safety Record",
      color: "success"
    },
    {
      icon: <FaHandshake />,
      title: "Personalized Service",
      description: "Customized itineraries tailored to your preferences, fitness level, and travel style.",
      stats: "95% Satisfaction Rate",
      color: "warning"
    },
    {
      icon: <FaMapMarkedAlt />,
      title: "Unique Destinations",
      description: "Access to remote and off-the-beaten-path locations with authentic cultural experiences.",
      stats: "50+ Hidden Gems",
      color: "info"
    },
    {
      icon: <FaLeaf />,
      title: "Sustainable Tourism",
      description: "Eco-friendly practices supporting local communities and environmental conservation.",
      stats: "Carbon-Neutral Tours",
      color: "eco"
    },
    {
      icon: <FaGlobe />,
      title: "International Standards",
      description: "Multilingual guides, international payment options, and global customer support.",
      stats: "30+ Countries Served",
      color: "global"
    },
    {
      icon: <FaClock />,
      title: "24/7 Support",
      description: "Round-the-clock assistance before, during, and after your adventure journey.",
      stats: "Instant Response",
      color: "support"
    }
  ];

  const stats = [
    { number: "5000+", label: "Happy Travelers", icon: <FaUsers /> },
    { number: "10+", label: "Years Experience", icon: <FaAward /> },
    { number: "98%", label: "Satisfaction Rate", icon: <FaStar /> },
    { number: "24/7", label: "Customer Support", icon: <FaClock /> }
  ];

  const testimonials = [
    {
      quote: "The K2 Base Camp trek was flawlessly organized. Safety was paramount, and the cultural experiences were authentic.",
      author: "Michael Chen",
      role: "Adventure Traveler, Singapore",
      rating: 5
    },
    {
      quote: "Personalized attention to detail made our Hunza Valley tour exceptional. The team went above and beyond!",
      author: "Sarah Johnson",
      role: "Photographer, USA",
      rating: 5
    }
  ];

  return (
    <section className="why-choose-us-section">
      <div className="container">
        {/* Section Header */}
        <div className="section-header">
          <span className="section-label">Why Choose Us</span>
          <h2 className="section-title">Your Trusted Adventure Partner</h2>
          <p className="section-description">
            Discover why thousands of travelers choose World Adventures Tours for their 
            Pakistan adventure experiences.
          </p>
        </div>

        {/* Stats Counter */}
        <div className="stats-counter">
          {stats.map((stat, index) => (
            <div className="stat-item" key={index}>
              <div className="stat-icon">{stat.icon}</div>
              <div className="stat-content">
                <span className="stat-number">{stat.number}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="features-grid">
          {features.map((feature, index) => (
            <div 
              className={`feature-card feature-${feature.color}`} 
              key={index}
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <div className="feature-icon-wrapper">
                <div className="feature-icon">{feature.icon}</div>
                <div className="feature-badge">{feature.stats}</div>
              </div>
              <div className="feature-content">
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                <div className="feature-highlight">
                  <FiCheckCircle /> Included in all tours
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="trust-section">
          <div className="trust-content">
            <div className="trust-header">
              <FaHandshake />
              <h3>Built on Trust & Integrity</h3>
            </div>
            <div className="trust-points">
              <div className="trust-point">
                <FiLock />
                <div>
                  <h4>Transparent Pricing</h4>
                  <p>No hidden charges, clear breakdown of all costs upfront</p>
                </div>
              </div>
              <div className="trust-point">
                <FiUserCheck />
                <div>
                  <h4>Verified Reviews</h4>
                  <p>Authentic feedback from verified travelers on multiple platforms</p>
                </div>
              </div>
              <div className="trust-point">
                <FiGlobe />
                <div>
                  <h4>Global Recognition</h4>
                  <p>Featured in international travel magazines and blogs</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="testimonial-sidebar">
            <div className="testimonial-header">
              <FaStar />
              <h4>Traveler Feedback</h4>
            </div>
            {testimonials.map((testimonial, index) => (
              <div className="mini-testimonial" key={index}>
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </div>
                <p className="testimonial-quote">"{testimonial.quote}"</p>
                <div className="testimonial-author">
                  <strong>{testimonial.author}</strong>
                  <span>{testimonial.role}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Banner */}
        <div className="cta-banner">
          <div className="cta-content">
            <div className="cta-icon">
              <FaHeart />
            </div>
            <div className="cta-text">
              <h3>Ready for Your Adventure?</h3>
              <p>Join our community of satisfied travelers and experience Pakistan like never before</p>
            </div>
            <div className="cta-actions">
              <a href="/contact" className="btn-primary">
                Start Your Journey <FiTrendingUp />
              </a>
              <a href="/about" className="btn-outline">
                Learn More About Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;